import { createTimeInstance } from '@utils/time';
import {
  CATALOG_ITEM_STATUSES,
  CONFIDENCE,
  LOAD_ACTIONS,
  PROPOSAL_STATUSES,
  REVIEWER_STATUSES,
  RUN_STATUSES,
  SETUP_STATUSES,
  UNMAPPED_REASON_PREFIX,
  VALIDATION_STATUSES,
} from '@services/fmdc';

// Human labels and formatters for the fmdc feature. Every surface that shows a status, reason,
// count, value or date routes through here so one machine value never renders two ways (the rule
// scoreBackfill/format.js already applies).

export const EM_DASH = '—';

export const humanizeToken = (value) =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// `stuck` is a display-only status: on the wire a stuck run keeps its stage with `stuck: true`.
export const DISPLAY_STUCK = 'stuck';

export const RUN_STATUS_LABELS = {
  [RUN_STATUSES.PENDING]: 'Pending',
  [RUN_STATUSES.INVENTORY]: 'Inventory',
  [RUN_STATUSES.HEADERS]: 'Headers',
  [RUN_STATUSES.CLASSIFY]: 'Classifying',
  [RUN_STATUSES.NORMALIZE]: 'Normalizing',
  [RUN_STATUSES.VALIDATE]: 'Validating',
  [RUN_STATUSES.REVIEW]: 'Needs review',
  [RUN_STATUSES.LOADABLE]: 'Loadable',
  [RUN_STATUSES.ERROR]: 'Error',
  [DISPLAY_STUCK]: 'Stuck',
};

export const deriveRunDisplayStatus = (run) => {
  if (!run) return null;
  if (run.stuck === true) return DISPLAY_STUCK;
  return run.status;
};

export const formatRunStatus = (status) => RUN_STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

// What the stepper calls each stage. `pending` gets the long name because on dev a run sits there
// for 2–3 minutes with nothing else visible.
export const STAGE_LABELS = {
  [RUN_STATUSES.PENDING]: 'Ingesting document',
  [RUN_STATUSES.INVENTORY]: 'Inventory',
  [RUN_STATUSES.HEADERS]: 'Headers',
  [RUN_STATUSES.CLASSIFY]: 'Classify',
  [RUN_STATUSES.NORMALIZE]: 'Normalize',
  [RUN_STATUSES.VALIDATE]: 'Validate',
};

export const formatStage = (stage) => STAGE_LABELS[stage] || humanizeToken(stage);

export const SETUP_STATUS_LABELS = {
  [SETUP_STATUSES.QUEUED]: 'Queued',
  [SETUP_STATUSES.RUNNING]: 'Setting up…',
  [SETUP_STATUSES.SUCCEEDED]: 'Succeeded',
  [SETUP_STATUSES.FAILED]: 'Failed',
};

export const formatSetupStatus = (status) => SETUP_STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

export const LOAD_ACTION_LABELS = {
  [LOAD_ACTIONS.LOAD_VALUE]: 'Load value',
  [LOAD_ACTIONS.LOAD_ASSUMPTION]: 'Load assumption',
  [LOAD_ACTIONS.REVIEW_ONLY]: 'Review only',
  [LOAD_ACTIONS.PROPOSE_NEW_CATALOG_ITEM]: 'Proposed catalog item',
  [LOAD_ACTIONS.DO_NOT_LOAD_VARIANCE]: 'Variance, not loaded',
};

export const formatLoadAction = (action) => LOAD_ACTION_LABELS[action] || (action ? humanizeToken(action) : EM_DASH);

export const REVIEWER_STATUS_LABELS = {
  [REVIEWER_STATUSES.PENDING]: 'Pending',
  [REVIEWER_STATUSES.MIXED]: 'Mixed',
  [REVIEWER_STATUSES.APPROVED]: 'Approved',
  [REVIEWER_STATUSES.REJECTED]: 'Rejected',
};

export const formatReviewerStatus = (status) =>
  REVIEWER_STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

export const CONFIDENCE_LABELS = {
  [CONFIDENCE.HIGH]: 'High',
  [CONFIDENCE.MEDIUM]: 'Medium',
  [CONFIDENCE.LOW]: 'Low',
};

export const formatConfidence = (confidence) =>
  CONFIDENCE_LABELS[confidence] || (confidence ? humanizeToken(confidence) : EM_DASH);

export const VALIDATION_STATUS_LABELS = {
  [VALIDATION_STATUSES.PASS]: 'Pass',
  [VALIDATION_STATUSES.WARNING]: 'Warning',
  [VALIDATION_STATUSES.FAIL]: 'Fail',
  [VALIDATION_STATUSES.NOT_CHECKED]: 'Not checked',
};

export const formatValidationStatus = (status) =>
  VALIDATION_STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

export const PROPOSAL_STATUS_LABELS = {
  [PROPOSAL_STATUSES.PENDING]: 'Pending',
  [PROPOSAL_STATUSES.APPROVED]: 'Approved',
  [PROPOSAL_STATUSES.REJECTED]: 'Rejected',
};

export const formatProposalStatus = (status) =>
  PROPOSAL_STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

export const CATALOG_STATUS_LABELS = {
  [CATALOG_ITEM_STATUSES.ACTIVE]: 'Active',
  [CATALOG_ITEM_STATUSES.PROPOSED]: 'Proposed',
  [CATALOG_ITEM_STATUSES.RETIRED]: 'Retired',
};

export const formatCatalogStatus = (status) =>
  CATALOG_STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

// Why a row is unmapped (`reason_unmapped`, and the part after `unmapped:` on a review reason).
export const UNMAPPED_REASON_LABELS = {
  llm_low_confidence: 'Low model confidence',
  llm_no_candidate: 'No candidate item',
  no_label: 'No row label',
  header_unresolved: 'Header unresolved',
  check_or_memo_row: 'Check or memo row',
  reviewer_rejected: 'Dismissed by a reviewer',
};

export const formatUnmappedReason = (reason) =>
  UNMAPPED_REASON_LABELS[reason] || (reason ? humanizeToken(reason) : EM_DASH);

export const REVIEW_REASON_LABELS = {
  validation_warning: 'Validation warning',
  validation_fail: 'Validation failed',
  confidence_medium: 'Medium confidence',
  confidence_low: 'Low confidence',
  custom_metric: 'Custom metric',
  requeued: 'Requeued',
};

export const isUnmappedReason = (reason) => typeof reason === 'string' && reason.startsWith(UNMAPPED_REASON_PREFIX);

// `unmapped:llm_no_candidate` → 'Unmapped: no candidate item'.
export const formatReviewReason = (reason) => {
  if (!reason) return EM_DASH;
  if (isUnmappedReason(reason)) {
    const inner = reason.slice(UNMAPPED_REASON_PREFIX.length);
    const label = formatUnmappedReason(inner);
    return `Unmapped: ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
  }
  return REVIEW_REASON_LABELS[reason] || humanizeToken(reason);
};

// The tone a reason chip takes: failures read bad, anything a reviewer is being asked to look at
// reads warn, bookkeeping reasons read neutral.
export const reviewReasonTone = (reason) => {
  if (reason === 'validation_fail' || reason === 'confidence_low') return 'bad';
  if (reason === 'validation_warning' || reason === 'confidence_medium' || isUnmappedReason(reason)) return 'warn';
  return 'neutral';
};

export const formatCount = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return EM_DASH;
  return Number(n).toLocaleString();
};

export const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return EM_DASH;
  const n = Number(bytes);
  if (!Number.isFinite(n)) return EM_DASH;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDateTime = (iso) => {
  if (!iso) return EM_DASH;
  const instance = createTimeInstance(iso);
  return instance && instance.isValid() ? instance.format('MMM D, YYYY h:mm A') : String(iso);
};

export const formatRelative = (iso) => {
  if (!iso) return EM_DASH;
  const instance = createTimeInstance(iso);
  return instance && instance.isValid() ? instance.fromNow() : String(iso);
};

// '2m 14s', '48s', '1h 03m'. From an ISO start to a millisecond `now`.
export const formatElapsed = (fromIso, nowMs = Date.now()) => {
  if (!fromIso) return EM_DASH;
  const start = createTimeInstance(fromIso);
  if (!start || !start.isValid()) return EM_DASH;
  const total = Math.max(0, Math.floor((nowMs - start.valueOf()) / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export const formatSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return EM_DASH;
  const n = Number(seconds);
  if (!Number.isFinite(n)) return EM_DASH;
  if (n < 60) return `${Math.round(n)}s`;
  const minutes = Math.floor(n / 60);
  return `${minutes}m ${Math.round(n % 60)}s`;
};

// Only `percent` is a fraction on the wire: every ratio-pattern row (growth_pct, margin,
// pct_of_revenue, cagr) is normalized to value_kind "percent" with normalized_units
// percent_decimal; days_or_turnover, multiple, currency, count stay as they are.
const isPercentKind = (valueKind) => valueKind === 'percent';

// A review group carries no value_kind, only the row's role and metric component. The pipeline
// gives every ratio-pattern row value_kind "percent", so the same rule is applied here to format
// its sample values.
// Not `share`: a share COUNT (shares_outstanding, diluted_shares) is a count, and `pct_of_*` /
// `market_share_pct` already match on `pct`.
const PERCENT_METRIC_PATTERN = /pct|margin|growth|cagr|percent|yield/;

export const valueKindForGroup = (group) => {
  if (!group) return undefined;
  if (group.row_role === 'ratio') return 'percent';
  if (typeof group.metric_component === 'string' && PERCENT_METRIC_PATTERN.test(group.metric_component))
    return 'percent';
  return undefined;
};

// A value as the queue and the detail show it. Numbers get thousands separators and at most two
// decimals; percent-kind numbers (growth, margins) are fractions on the wire and render as `%`.
// A null value falls back to what the model showed, verbatim — `#N/A`, `na` — because that IS
// the information for a review_only cell.
export const formatValue = (value, { valueKind, asReported } = {}) => {
  if (value === null || value === undefined) {
    if (asReported !== null && asReported !== undefined && asReported !== '') return String(asReported);
    return EM_DASH;
  }
  if (typeof value === 'number') {
    if (isPercentKind(valueKind)) return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
};

// 'FY24A −4.5%; FY25E 3.1%' from a group's sample_values.
export const formatSampleValues = (samples, { valueKind } = {}) => {
  if (!Array.isArray(samples) || samples.length === 0) return EM_DASH;
  return samples.map((s) => `${s.period || '?'} ${formatValue(s.value, { valueKind })}`).join('; ');
};

// 'Showing 51–100 of 491'.
export const formatShownRange = (offset, count, total) => {
  if (!total) return 'Showing 0 of 0';
  const from = offset + 1;
  const to = Math.min(offset + count, total);
  return `Showing ${formatCount(from)}–${formatCount(to)} of ${formatCount(total)}`;
};

// 'Updated just now' / 'Updated 12s ago' / 'Updated 3m ago', the backfill history label.
export const formatUpdatedAgo = (lastLoadedAt, nowMs = Date.now()) => {
  if (lastLoadedAt == null) return null;
  const secondsAgo = Math.max(0, Math.floor((nowMs - lastLoadedAt) / 1000));
  if (secondsAgo < 5) return 'Updated just now';
  if (secondsAgo >= 60) return `Updated ${Math.floor(secondsAgo / 60)}m ago`;
  return `Updated ${secondsAgo}s ago`;
};

export const pluralize = (count, singular, plural = `${singular}s`) => (count === 1 ? singular : plural);
